/**
 * Hook for managing sidebar state with localStorage persistence
 */

import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';

interface SidebarState {
  isCollapsed: boolean;
  isMobileOpen: boolean;
}

interface UseSidebarStateReturn {
  isCollapsed: boolean;
  isMobileOpen: boolean;
  toggleCollapsed: () => void;
  openMobile: () => void;
  closeMobile: () => void;
}

/**
 * Check if viewport is mobile size
 */
const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(query);
    const handler = (event: MediaQueryListEvent) => setMatches(event.matches);

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [query]);

  return matches;
};

/**
 * Custom hook for managing sidebar state with localStorage persistence
 */
export const useSidebarState = (): UseSidebarStateReturn => {
  const { user } = useAuth();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const getStorageKey = () => `kessan_sidebar_state_${user?.userId || 'guest'}`;

  const loadState = (): SidebarState => {
    try {
      const stored = localStorage.getItem(getStorageKey());
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading sidebar state:', error);
    }
    // Default state: collapsed on mobile, expanded on desktop
    return {
      isCollapsed: isMobile,
      isMobileOpen: false,
    };
  };

  const [state, setState] = useState<SidebarState>(loadState);

  const saveState = (newState: SidebarState) => {
    try {
      localStorage.setItem(getStorageKey(), JSON.stringify(newState));
    } catch (error) {
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        console.warn('LocalStorage quota exceeded, using in-memory state only');
      } else {
        console.error('Error saving sidebar state:', error);
      }
    }
  };

  const toggleCollapsed = () => {
    setState((prev) => {
      const newState = { ...prev, isCollapsed: !prev.isCollapsed };
      saveState(newState);
      return newState;
    });
  };

  const openMobile = () => {
    setState((prev) => ({ ...prev, isMobileOpen: true }));
  };

  const closeMobile = () => {
    setState((prev) => ({ ...prev, isMobileOpen: false }));
  };

  // Update collapsed state when viewport changes
  useEffect(() => {
    if (isMobile && !state.isCollapsed) {
      setState((prev) => {
        const newState = { ...prev, isCollapsed: true };
        saveState(newState);
        return newState;
      });
    }
  }, [isMobile]);

  return {
    isCollapsed: state.isCollapsed,
    isMobileOpen: state.isMobileOpen,
    toggleCollapsed,
    openMobile,
    closeMobile,
  };
};
